import { getOfflineMs, trackRequestEnd, trackRequestStart, waitForOnline } from './monitor';

const MAX_GET_RETRIES = 3;

let nextId = 0;

/** No response came back at all: the network, not the server, failed. A cancel is not a failure. */
export const isNetworkError = (error) =>
    Boolean(error) && !error.response && error.code !== 'ERR_CANCELED';

const isGet = (config) => config?.method === 'get';

/**
 * Connects an axios instance to the connection banner.
 *
 * - Every request is reported to the monitor, which is what lets a failed or long-running request
 *   bring up the Offline or Slow banner.
 * - A GET waits while the user is offline and goes out once the connection is back.
 * - With `retryGets`, a GET that fails with no response is sent again once the connection is back,
 *   but only when the user really went offline while it was out. `/health` does all the checking;
 *   the GET itself is sent once more, because the screen still needs its answer. When our API is
 *   down, or a blip clears without going offline, the failure reaches the caller as today.
 *
 * Nothing else is ever held or sent again: POST, PUT, PATCH and DELETE go out exactly as before,
 * and their failures reach the caller unchanged.
 */
export const attachConnectivity = (instance, { retryGets = false } = {}) => {
    instance.interceptors.request.use(async (config) => {
        if (isGet(config)) await waitForOnline();

        nextId += 1;
        config.connectivityId = nextId;
        config.offlineMsAtSend = getOfflineMs();
        trackRequestStart(nextId);
        return config;
    });

    instance.interceptors.response.use(
        (response) => {
            trackRequestEnd(response.config.connectivityId, { answered: true });
            return response;
        },
        async (error) => {
            const { config } = error;
            const networkError = isNetworkError(error);
            trackRequestEnd(config?.connectivityId, {
                answered: Boolean(error.response),
                networkError,
            });

            const retries = config?.connectivityRetries ?? 0;
            if (!retryGets || !networkError || !isGet(config) || retries >= MAX_GET_RETRIES) {
                throw error;
            }

            // Waits for the check this failure started, and for the connection if it found none.
            await waitForOnline();
            if (getOfflineMs() === config.offlineMsAtSend) throw error;

            return instance({ ...config, connectivityRetries: retries + 1 });
        }
    );
};
